import { Loader2Icon } from "lucide-react"
import { useLayoutEffect, useRef } from "react"
import { Button } from "@/primitives/button.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { Kbd } from "./key.tsx"
import { useLadder } from "./page-chrome.tsx"
import { scrollFieldClear, useDockedBar } from "./save-bar-dock.ts"
import { useUnsavedWork } from "./unsaved-work.ts"
import { useKeyboardUp, useViewport } from "./viewport.tsx"

/*
 * The bar a settings form raises while it holds changes nobody has saved: it floats at the foot of
 * the page as the Member scrolls, says what changed, and offers Discard and Save. A form with no
 * changes shows none, so the page renders it only while dirty.
 *
 * Save, when the changes can't be saved as they stand, is disabled and says why in its tooltip. A
 * disabled control can't be tapped, so on touch the reason is a line under the summary instead.
 *
 * On `compact` it is a full-width bar docked at the foot of the screen: on the tab bar, or on the
 * on-screen keyboard while it is up, so the Member saves while they type. A page's bars dock one
 * above another (`save-bar-dock.ts`), and its scroll container pads its foot by them while they
 * show, so nothing ends under them. Docking on the field being typed in, or lifted onto it with the
 * keyboard, a bar scrolls the page until the line being typed on stands clear of it, but never so
 * far that the field's top goes under the page's stuck chrome. While it shows, the page holds
 * unsaved work, which an update waits on.
 *
 * ⌘↵ saves and Esc discards while focus is in the form the bar belongs to: the `<form>` it sits in,
 * or else the element it sits in. Discarding takes the bar away, so a Discard pressed in the bar
 * hands the focus back to the form's first control rather than dropping it to the page. On touch it
 * lets the focus go instead: a field focused there raises the keyboard and scrolls the page away.
 */

const focusable =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/** The first control of `form` that takes the focus, outside the bar. */
const firstControlOf = (form: Element | null, bar: Element | null) =>
  [...(form?.querySelectorAll<HTMLElement>(focusable) ?? [])].find(
    (control) => !bar?.contains(control),
  )

export function SaveBar({
  summary,
  onDiscard,
  onSave,
  saving,
  invalidReason,
}: {
  /** What changed, such as "Name and description". */
  summary: string
  onDiscard: () => void
  onSave: () => void
  saving: boolean
  /** Why the changes can't be saved as they stand; Save is disabled and says this. */
  invalidReason?: string
}) {
  const bar = useRef<HTMLDivElement>(null)
  const form = useRef<Element | null>(null)
  const keyboardUp = useKeyboardUp()
  const { compact, touch } = useViewport()
  useUnsavedWork(true)
  useLayoutEffect(() => {
    form.current = bar.current?.closest("form") ?? bar.current?.parentElement ?? null
  })

  // Docked, it stands above the page's bars that showed before it, and moves as they come and go.
  const below = useDockedBar(bar, compact)

  // After the frame, so the bar stands where the keyboard and the bars below it put it.
  const { stuck } = useLadder()
  const stuckNow = useRef(stuck)
  stuckNow.current = stuck
  useLayoutEffect(() => {
    if (!compact) return
    const frame = requestAnimationFrame(() =>
      scrollFieldClear(bar.current, form.current, stuckNow.current),
    )
    return () => cancelAnimationFrame(frame)
  }, [compact, keyboardUp, below])

  const canSave = !saving && invalidReason === undefined
  const discard = () => {
    const fromBar = bar.current?.contains(document.activeElement) ?? false
    if (fromBar && touch && document.activeElement instanceof HTMLElement)
      document.activeElement.blur()
    onDiscard()
    if (fromBar && !touch) firstControlOf(form.current, bar.current)?.focus()
  }
  useShortcuts(
    {
      submit: () => {
        if (canSave) onSave()
      },
      discard: () => {
        if (!saving) discard()
      },
    },
    { within: form },
  )

  const save = (
    // Saving, it keeps its colour and the focus, and takes no second press.
    <Button size="sm" disabled={!canSave} focusableWhenDisabled={saving} onClick={onSave}>
      {saving && <Loader2Icon aria-hidden className="animate-spin" />}
      Save
      {!saving && <Kbd shortcut="submit" />}
    </Button>
  )

  return (
    <div
      ref={bar}
      role="region"
      aria-label="Unsaved changes"
      data-slot="save-bar"
      // A fixed bar's bottom margin lifts it off the foot, above the bars docked below it.
      style={below > 0 ? { marginBottom: below } : undefined}
      className={cn(
        "sticky bottom-4 z-20 mx-auto mt-6 flex h-11 w-fit animate-in items-center gap-3 rounded-lg bg-popover px-3 shadow-dialog duration-150 ease-snappy fade-in slide-in-from-bottom-2 touch:h-auto touch:min-h-11 touch:py-1.5",
        "compact:fixed compact:inset-x-0 compact:z-40 compact:m-0 compact:h-auto compact:min-h-14 compact:w-auto compact:rounded-none compact:border-t compact:border-border-subtle compact:px-4 compact:py-2 compact:shadow-none",
        // `touch:` utilities are emitted after `compact:` ones, so a phone's own geometry is
        // stacked on both to beat the touch pill's.
        "compact:touch:min-h-14 compact:touch:py-2",
        // Docked, it moves to the keyboard and over the bars below it at once: `duration-150`
        // would slide every property, and the page would scroll by where the bar stood mid-slide.
        "compact:transition-none",
        keyboardUp ? "compact:bottom-keyboard" : "compact:bottom-shell",
      )}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-primary" />
      <div className="contents touch:flex touch:min-w-0 touch:flex-col compact:flex compact:min-w-0 compact:flex-1 compact:flex-col">
        <p className="max-w-md truncate text-ui">
          <span className="font-medium">Unsaved</span>
          <span className="text-muted-foreground"> · {summary}</span>
        </p>
        {invalidReason !== undefined && (
          <p className="hidden max-w-md text-xs text-muted-foreground touch:block">
            {invalidReason}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button variant="ghost" size="sm" disabled={saving} onClick={discard}>
          Discard
        </Button>
        {invalidReason === undefined ? (
          save
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  tabIndex={0}
                  className="inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              }
            >
              {save}
            </TooltipTrigger>
            <TooltipContent>{invalidReason}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
